<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { fetchInlineAttachment, type Todo, type TodoAttachment } from '@/api'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'
import { useTodosStore } from '@/stores/todos'
import { confirmDialog, openExternalLink } from '@/telegramWebApp'
import { formatBytes } from '@/todos/text'

const props = defineProps<{ todo: Todo; calendarId: number; readonly: boolean }>()

const { t } = useI18n()
const store = useTodosStore()
const toast = useToast()
const linkDraft = ref('')
const busy = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

function label(attachment: TodoAttachment): string {
  if (attachment.filename) return attachment.filename
  if (!attachment.uri) return t('attachments.numbered', { n: attachment.index + 1 })
  if (/^(mid|message|imap|mailbox):/i.test(attachment.uri)) return t('attachments.mailLink')
  try {
    const url = new URL(attachment.uri)
    return url.hostname + (url.pathname.length > 1 ? url.pathname : '')
  } catch {
    return attachment.uri
  }
}

async function open(attachment: TodoAttachment, download = false): Promise<void> {
  if (attachment.uri) {
    openExternalLink(
      download && attachment.uri.includes('/api/files/')
        ? `${attachment.uri}&dl=1`
        : attachment.uri,
    )
    return
  }
  try {
    const blob = await fetchInlineAttachment(props.calendarId, props.todo.uid, attachment.index)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = attachment.filename ?? 'attachment'
    anchor.click()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (e) {
    reportError(e, 'attachments.downloadFailed')
    toast.error(e, t('attachments.downloadFailed'))
  }
}

async function run(work: () => Promise<unknown>, failure: string): Promise<void> {
  busy.value = true
  try {
    await work()
  } catch (e) {
    reportError(e, 'attachments.work')
    toast.error(e, failure)
  } finally {
    busy.value = false
  }
}

function addLink(): void {
  const uri = linkDraft.value.trim()
  if (!uri) return
  void run(async () => {
    await store.addLink(props.calendarId, props.todo.uid, uri)
    linkDraft.value = ''
  }, t('attachments.addFailed'))
}

function upload(event: Event): void {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return
  void run(async () => {
    await store.upload(props.calendarId, props.todo.uid, file)
    toast.info(t('attachments.attached', { name: file.name }))
  }, t('attachments.uploadFailed')).finally(() => {
    if (fileInput.value) fileInput.value.value = ''
  })
}

async function remove(attachment: TodoAttachment): Promise<void> {
  if (!(await confirmDialog(t('attachments.removeConfirm', { name: label(attachment) })))) return
  await run(
    () => store.removeAttachment(props.calendarId, props.todo.uid, attachment.index),
    t('attachments.removeFailed'),
  )
}
</script>

<template>
  <div class="attachments">
    <ul v-if="todo.attachments.length" class="list">
      <li v-for="attachment in todo.attachments" :key="attachment.index">
        <button type="button" class="name" @click="open(attachment)">
          {{ attachment.uri ? '🔗' : '📎' }} {{ label(attachment) }}
          <span v-if="attachment.size !== null" class="ui-hint">{{
            formatBytes(attachment.size)
          }}</span>
        </button>
        <button
          type="button"
          class="ui-btn ui-btn-icon"
          :aria-label="t('common.download')"
          :title="t('common.download')"
          @click="open(attachment, true)"
        >
          ⬇
        </button>
        <button
          v-if="!readonly"
          type="button"
          class="ui-btn ui-btn-icon"
          :aria-label="t('common.remove')"
          :disabled="busy"
          @click="remove(attachment)"
        >
          ✕
        </button>
      </li>
    </ul>
    <p v-else class="ui-hint">{{ t('attachments.none') }}</p>
    <template v-if="!readonly">
      <form class="add" @submit.prevent="addLink">
        <input
          v-model="linkDraft"
          class="ui-input"
          :placeholder="t('attachments.linkPlaceholder')"
          inputmode="url"
          :disabled="busy"
        />
        <button type="submit" class="ui-btn" :disabled="busy || !linkDraft.trim()">
          {{ t('attachments.addLink') }}
        </button>
      </form>
      <label class="ui-btn upload" :class="{ busy }">
        {{ busy ? t('attachments.working') : t('attachments.upload') }}
        <input ref="fileInput" type="file" hidden :disabled="busy" @change="upload" />
      </label>
      <p class="ui-hint">
        {{ t('attachments.storageHint') }}
      </p>
    </template>
  </div>
</template>

<style scoped>
.attachments {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.3rem 0;
}

.list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.list li {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding: 0.3rem 0;
  border: none;
  background: none;
  color: var(--tg-link-color);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  font: inherit;
}

.add {
  display: flex;
  gap: 0.4rem;
}

.upload {
  align-self: flex-start;
}

.upload.busy {
  opacity: 0.6;
}

p {
  margin: 0;
}
</style>
